import React from 'react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../../../components/LandingNav';
import { BLOG_POSTS } from '../../../config/blog-posts';
import '../../../styles/blog.css';

const tikTokLinks = [
  {
    id: 'video-1',
    label: 'Video 1 — “Chicken Banana” (Apr 19, 2025)',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7495047152926739767?lang=en',
    stats: ['49.5M views', '3.4M likes', '7,398 comments', '139.5k saves'],
    note:
      'Employees using the “Chicken Banana” sound while scanning items. A masterclass in trend-jacking + in-store authenticity.',
  },
  {
    id: 'video-2',
    label: 'Video 2 — “Big Guy” (Ice Spice / SpongeBob)',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7585394724387769620?lang=en',
    stats: ['52.4k views', '958 likes', '18 comments', '39 saves'],
    note: 'Two employees doing the trend dance — pure lo‑fi energy.',
  },
  {
    id: 'video-3',
    label: 'Video 3 — “Drizzilicious available now”',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7574908671948311828?lang=en',
    stats: ['1.3M views', '117.3k likes', '1,030 comments', '710 saves'],
    note:
      'Product launch format: employee showcases flavours as a “soccer announcer” sound calls out names.',
  },
  {
    id: 'video-4',
    label: 'Video 4 — Chips shelf dance',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7477031787059793158?lang=en',
    stats: ['106.8k views', '2,164 likes', '41 comments', '144 saves'],
    note: 'Product-as-backdrop (environmental anchoring).',
  },
  {
    id: 'video-5',
    label: 'Video 5 — Kendrick “They Not Like Us”',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7471797594126044422?lang=en',
    stats: ['169.3k views', '2,957 likes', '54 comments', '101 saves'],
    note:
      'Stockroom/operations as the stage; comments show “retail tourism” forming (“I would shop here just to see this”).',
  },
  {
    id: 'video-6',
    label: 'Video 6 — “Shake it to the max” (owner cameo)',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7507803821385469189?lang=en',
    stats: ['106.1k views', '4,131 likes', '57 comments', '182 saves'],
    note: 'Owner appears with employees — leadership-as-participation.',
  },
  {
    id: 'video-7',
    label: 'Video 7 — “Hit of the Month” (Boom Chicka Pop)',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7493694835661016325?lang=en',
    stats: ['33.9k views', '503 likes', '15 comments', '21 saves'],
    note: 'Another product-prop moment: fast, simple, repeatable.',
  },
  {
    id: 'video-8',
    label: 'Video 8 — “Just stop for a minute and smile”',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7484817290278522118?lang=en',
    stats: ['119.5k views', '5,058 likes', '95 comments', '161 saves'],
    note: 'Culture/positivity — turning “shopping is a chore” into “shopping is a vibe.”',
  },
  {
    id: 'video-9',
    label: 'Video 9 — Owner + staff dance (culture building)',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7479256054392474935?lang=en',
    stats: ['154.4k views', '4,499 likes', '71 comments', '131 saves'],
    note: 'Comments explicitly praise retention + management (“teach classes on how to be a manager…”).',
  },
  {
    id: 'video-10',
    label: 'Video 10 — Customer joins dance (community integration)',
    url: 'https://www.tiktok.com/@joeandvalsnofrills/video/7474295729943596343?lang=en',
    stats: ['384.1k views', '5,160 likes', '114 comments', '198 saves'],
    note: 'Opt‑in customer moment; community + local affinity.',
  },
];

export default function NoFrillsBlogPage() {
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === 'no-frills');

  // Should never happen (we ship meta for the post), but keep safe.
  if (!post) {
    return (
      <div className="landing-page">
        <LandingNav />
        <main className="blog-container">
          <div className="blog-header">
            <h1 className="blog-title">Blog post not found</h1>
            <p className="blog-subtitle">This post isn’t available yet.</p>
            <div className="blog-header-actions">
              <button className="blog-btn secondary" onClick={() => navigate('/blog')}>
                Back to Blog
              </button>
              <button className="blog-btn primary" onClick={() => navigate('/contact')}>
                Contact
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <LandingNav />
      <main className="blog-container">
        <header className="blog-post-hero">
          <button className="blog-backlink" onClick={() => navigate('/blog')}>
            ← Back to Blog
          </button>

          <div className="blog-post-meta">
            {post.logoSrc && (
              <div className="blog-post-logo">
                <img src={post.logoSrc} alt={post.logoAlt || post.brand || 'Brand'} />
              </div>
            )}
            <div>
              <div className="blog-post-kicker">
                {post.brand ? `${post.brand} case study` : 'Blog'} {post.dateLabel ? `• ${post.dateLabel}` : ''}
              </div>
              <h1 className="blog-post-title">{post.title}</h1>
              <p className="blog-post-excerpt">{post.excerpt}</p>
            </div>
          </div>
        </header>

        <article className="blog-post-body">
          <section className="blog-post-section blog-toc">
            <div className="blog-toc-title">Table of Contents</div>
            <ol>
              <li>
                <a href="#trust-deficit">The Trust Deficit: Why 2026 Consumers Reject "Polish"</a>
              </li>
              <li>
                <a href="#retention-proof">The 3-Year Retention Proof</a>
              </li>
              <li>
                <a href="#decentralized-playbook">The Decentralized Playbook: Autonomy as a Competitive Moat</a>
              </li>
              <li>
                <a href="#golden-ratio">Data Audit: Deconstructing the 70M+ View “Golden Ratio”</a>
              </li>
              <li>
                <a href="#visual-semiotics">Visual Semiotics: Why the “Backroom” is the New Digital Shelf</a>
              </li>
              <li>
                <a href="#operational-sop">Operational SOP: Step-by-Step Implementation</a>
              </li>
              <li>
                <a href="#risk-mitigation">The Risk-Mitigation Matrix: Safety &amp; Brand Guardrails</a>
              </li>
              <li>
                <a href="#human-moat">The Strategic Long-Game: Building the “Human Moat”</a>
              </li>
              <li>
                <a href="#final-verdict">Final Verdict: The Post-Corporate Era</a>
              </li>
              <li>
                <a href="#scaling-framework">Scaling the Framework: Don’t Just Watch the Trend</a>
              </li>
              <li>
                <a href="#content-under-analysis">List of content under analysis</a>
              </li>
            </ol>
          </section>

          <section className="blog-post-section blog-callout">
            <p style={{ marginTop: 0 }}>
              <strong>Premise:</strong> In an era of AI-generated content and declining trust, Joe &amp; Val’s No Frills has built a
              “Human Moat” that traditional retail marketing cannot breach. By decentralizing creative control and empowering
              employees as brand protagonists, this location transformed the grocery chore into a destination experience.
            </p>
            <p style={{ marginBottom: 0 }}>
              This guide breaks down the Employee Generated Content Framework that drove <strong>70M+ views</strong> across{' '}
              <strong>124 TikTok posts in 2025</strong> and a measurable <strong>“Alive Store Effect.”</strong>
            </p>
          </section>

          <section className="blog-post-section" id="trust-deficit">
            <h2>1. The Trust Deficit: Why 2026 Consumers Reject "Polish"</h2>
            <p>
              The retail landscape has officially shifted from the "<strong>Brand as Institution</strong>" phase to the "<strong>Brand as Neighbor</strong>" phase. In 2026, the "Corporate Wall" - the barrier of polished ads, scripted spokespeople, and stock photography is no longer a sign of professionalism. It is a sign of inauthenticity.
            </p>
            <ul>
              <li>
                <strong>The Rise of "Lo-Fi" Truth:</strong> Consumers now equate high production value with "being sold to."
                Conversely, handheld, raw video content (EGC - Employee Generated Content) is the universal signal for Experience and Trustworthiness.
              </li>
              <li>
                <strong>The Franchisee Advantage:</strong> Corporate-led marketing is inherently broad. Joe &amp; Val have used their
                Aurora, Ontario location to prove that Hyper-Local Context &gt; National Reach. By treating their store as a "Digital Front Porch," they have reduced the friction between the digital scroll and the physical stroll into the store.
              </li>
              <li>
                <strong>Retail Tourism:</strong> We are seeing a trend where shoppers drive past three competitors to visit a specific
                "Alive Store." They aren't just buying bananas; they are participating in a culture they’ve already joined digitally.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="retention-proof">
            <h2>2. The 3-Year Retention Proof</h2>
            <p>
              One of the most significant insights from the Joe &amp; Val data set is the longevity of the cast. An employee
              featured in their viral 2022 content is still a primary protagonist in 2025. In a sector plagued by 60%+ annual
              turnover, this 3+ year tenure is a statistical anomaly.
            </p>
            <h3>The "Protagonist Effect"</h3>
            <p>
              When an entry-level staffer is empowered to lead a TikTok trend (like the "Chicken Banana" sound in <a href="#content-under-analysis">Video 1</a>),
              their psychological contract with the employer changes. They are no longer "Transactional Labor"; they are Brand
              Protagonists.
            </p>
            <ul>
              <li>
                <strong>Internal Brand Advocacy:</strong> This agency fosters a sense of ownership. If an employee is the face of a store that has 50 million fans, they aren't just an "associate" - they are an influencer with a physical headquarters.
              </li>
              <li>
                <strong>The Cost of Turnover:</strong> Replacing a retail employee costs approximately $1,500 - $4,000 in recruitment and training. By building a "Content Culture," Joe &amp; Val have effectively lowered their P&amp;L "leakage" by keeping their best talent engaged through creative expression.
              </li>
              <li>
                <strong>EVP (Employee Value Proposition):</strong> In 2026, the best talent doesn't just want a paycheck; they want a platform. Joe &amp; Val provide both.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="decentralized-playbook">
            <h2>3. The Decentralized Playbook: Autonomy as a Competitive Moat</h2>
            <p>
              The "Joe &amp; Val" model is a systematic dismantling of traditional corporate gatekeeping. Most retail failures on social media are caused by <strong>"The Approval Bottleneck."</strong>
            </p>
            <h3>The 80/20 Governance Model</h3>
            <p>
              We hypothesize that the Loblaw-Franchisee relationship here operates on an 80/20 split:
            </p>
            <ol>
              <li>
                <strong>20% Corporate (The Yellow Backdrop):</strong> Corporate provides the brand assets, the "Hauler" logo, and the national price-drop alerts.
              </li>
              <li>
                <strong>80% Local (The Vibe):</strong> Joe &amp; Val own the creative execution.
              </li>
            </ol>
            <ul>
              <li>
              <strong>Speed-to-Market:</strong> Real-time trends (like the Kendrick Lamar "They Not Like Us" dance in <a href="#content-under-analysis">Video 5</a>) have a shelf-life of 48–72 hours. A corporate legal review takes 14 days. By the time a traditional brand "approves" a trend, the algorithm has moved on. Joe &amp; Val’s Decentralized Autonomy allows them to "trend-jack" while the iron is hot.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="golden-ratio">
            <h2>4. Data Audit: Deconstructing the 70M+ View “Golden Ratio”</h2>
            <p>
              This analysis of 50+ posts reveals a deliberate "Golden Ratio" of content types. They aren't just "making videos";
              they are managing a digital ecosystem.
            </p>

            <div className="blog-table-wrapper" aria-label="Golden ratio table">
              <table className="blog-table">
                <thead>
                  <tr>
                    <th>Content Pillar</th>
                    <th>Representative Video</th>
                    <th>Strategic Objective</th>
                    <th>Performance Metric</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Trend-Jacking (Most Content)</td>
                    <td>Chicken Banana (<a href="#content-under-analysis">Video 1</a>)</td>
                    <td>Algorithmic Reach (TOFU)</td>
                    <td>49.5M Views</td>
                  </tr>
                  <tr>
                    <td>Product Launch</td>
                    <td>Drizzilicious (<a href="#content-under-analysis">Video 3</a>)</td>
                    <td>Inventory Velocity (MOFU)</td>
                    <td>1.3M Views</td>
                  </tr>
                  <tr>
                    <td>Culture Building</td>
                    <td>Owner/Staff Dance (<a href="#content-under-analysis">Video 9</a>)</td>
                    <td>Retention/Recruitment</td>
                    <td>154k Views</td>
                  </tr>
                  <tr>
                    <td>Community Integration</td>
                    <td>Customer Dance (<a href="#content-under-analysis">Video 10</a>)</td>
                    <td>Local Brand Affinity</td>
                    <td>384k Views</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: 22, marginBottom: 10 }}>The Earned Media Value (EMV) Analysis</h3>
            <p><a href="#content-under-analysis">Video 1</a>’s 49.5 Million views is a masterclass in <strong>Efficiency</strong>.</p>
            <ul>
              <li>
                <strong>Traditional Spend:</strong> To reach 49M people via a National TV spot or Meta Ads would cost approximately
                $250,000 - $500,000 in media spend alone.
              </li>
              <li>
                <strong>Joe &amp; Val Spend:</strong> 15 minutes of employee time + $0 in production costs.
              </li>
              <li>
                <strong>The Verdict:</strong> Localized, authentic content is currently the highest-ROI asset in the retail P&amp;L.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="visual-semiotics">
            <h2>5. Visual Semiotics: Why the "Backroom" is the New Digital Shelf</h2>
            <p>Joe &amp; Val use a specific visual language that signals <strong>Radical Transparency</strong>.</p>
            <ul>
              <li>
                <strong>The Aesthetics of the "Working" Store:</strong> <a href="#content-under-analysis">Video 5</a> (moving stock) and <a href="#content-under-analysis">Video 1</a> (scanning items) use the
                "Backstage" as the stage. To the Gen Z and Millennial shopper, a "messy" stockroom is more trustworthy than a
                sterile, AI-generated kitchen. It proves the store is actually operating.
              </li>
              <li>
                <strong>The "Owner-Leader" Cameo:</strong> In <a href="#content-under-analysis">Video 6</a> and <a href="#content-under-analysis">Video 9</a>, seeing Joe &amp; Val dance alongside entry-level
                staff is a high-authority leadership move. It signals a <strong>Flat Organizational Culture</strong>. This
                "Peer-to-Peer" leadership style is the #1 driver for Gen Z employee satisfaction in 2026.
              </li>
              <li>
                <strong>Environmental Anchoring:</strong> They don't use "Shop Now" buttons. They use the products as the backdrop
                (<a href="#content-under-analysis">Video 4</a>). This creates a subconscious link: <strong>Fun = No Frills.</strong>
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="operational-sop">
            <h2>6. Operational SOP: Step-by-Step Implementation</h2>
            <p>
              To replicate the "Joe &amp; Val" effect, an organization must transition from a culture of{' '}
              <strong>command-and-control</strong> to a culture of <strong>empowerment-and-governance</strong>. This is not a
              "marketing" task; it is an <strong>operational pivot</strong>.
            </p>

            <h3>Step 1: The Internal Talent Audit</h3>
            <p>
              Do not hire an outside agency to "act" like your employees. Identify the <strong>Digital Natives</strong> already on
              your payroll. These are your "Content Captains."
            </p>
            <ul>
              <li>
                <strong>Selection Criteria:</strong> Look for employees with high internal social capital and a native
                understanding of short-form video rhythm (pacing, transitions, and audio-syncing).
              </li>
              <li>
                <strong>The "3-Year Tenure" Insight:</strong> By identifying long-term staff, you maintain brand continuity. As
                seen with Joe &amp; Val, seeing the same faces over 36 months builds deep, community-level trust.
              </li>
            </ul>

            <h3>Step 2: The 15-Minute "Content Window"</h3>
            <p>Content creation must be treated with the same operational rigor as inventory restocking or safety checks.</p>
            <ul>
              <li>
                <strong>Scheduling:</strong> Dedicate 15-minute blocks during "Low-Traffic/High-Vibe" periods (e.g., Tuesday
                mid-mornings).
              </li>
              <li>
                <strong>The Permission Layer:</strong> The owner/manager must be physically present or verbally supportive. This
                removes the "fear of being reprimanded" for having a phone out on the floor.
              </li>
            </ul>

            <h3>Step 3: Trend-Mapping (The "Fastvertising" Loop)</h3>
            <p>In 2026, social media moves in 48-hour cycles.</p>
            <ul>
              <li>
                <strong>The Discovery Phase:</strong> Content Captains spend 5 minutes daily scanning trending audio.
              </li>
              <li>
                <strong>The Execution Phase:</strong> Video is shot on-site immediately. No scripts. No teleprompters.
              </li>
              <li>
                <strong>The "Sound-First" Rule:</strong> As seen in <a href="#content-under-analysis">Video 1</a> (Chicken Banana), the audio is the hook. The visual is
                simply the delivery mechanism.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="risk-mitigation">
            <h2>7. The Risk-Mitigation Matrix: Safety &amp; Brand Guardrails</h2>
            <p>
              Authority comes from knowing where the boundaries are. To scale this to 500+ locations, corporate needs a "Safety
              Guardrail Framework" that protects the P&amp;L without stifling the "vibe."
            </p>

            <div className="blog-table-wrapper" aria-label="Risk mitigation matrix table">
              <table className="blog-table">
                <thead>
                  <tr>
                    <th>Risk Category</th>
                    <th>The "Hard" Rule</th>
                    <th>The "Joe &amp; Val" Solution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Physical Safety</td>
                    <td>No filming near heavy machinery.</td>
                    <td>Keep dances to wide aisles or stockroom "clear zones."</td>
                  </tr>
                  <tr>
                    <td>Customer Privacy</td>
                    <td>Zero filming of bystanders.</td>
                    <td>
                      Use angles that focus on staff; customers join only via "Opt-in" (<a href="#content-under-analysis">Video 10</a>).
                    </td>
                  </tr>
                  <tr>
                    <td>Brand Integrity</td>
                    <td>No mocking products or prices.</td>
                    <td>Use products as "co-stars" in the content (Environmental Anchoring).</td>
                  </tr>
                  <tr>
                    <td>Operational Flow</td>
                    <td>Service &gt; social.</td>
                    <td>Content is never filmed while there is a queue at the checkout.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>The "Owner-as-Safety-Net"</h3>
            <p>
              When Joe or Val appear in a video, they aren't just dancing - they are performing a <strong>live audit</strong>.
              Their presence ensures that the content remains "brand-safe" while maintaining the energy of the staff. This is
              the <strong>Management-by-Walking-Around (MBWA)</strong> model updated for the TikTok era.
            </p>
          </section>

          <section className="blog-post-section" id="human-moat">
            <h2>8. The Strategic Long-Game: Building the "Human Moat"</h2>
            <p>
              As we look toward the remainder of 2026 and beyond, the retail sector faces a choice: <strong>Total Automation</strong>{' '}
              or <strong>Radical Humanity</strong>.
            </p>
            <h3>The Inevitability of Automation</h3>
            <p>
              Checkouts, shelf-audits, and logistics are being handled by AI. This levels the playing field on "Functionality." If
              every store is perfectly efficient, "Functionality" ceases to be a competitive advantage.
            </p>
            <h3>The Survival of the "Alive Store"</h3>
            <p>
              The only thing a competitor cannot replicate - and a robot cannot simulate, is the <strong>collective personality of a local team</strong>. <strong>Social Proof = Brand Equity:</strong> Joe &amp; Val have proven that 49.5M views on 1 video isn't just a "viral moment"; it’s a global signal of local health.
            </p>
            <ul>
              <li>
                <strong>The Retention Dividend:</strong> By turning a "job" into a "platform," they have solved the #1 problem in retail: <strong>The Talent War.</strong> Employees don't leave Joe &amp; Val because they aren't just staffers - they are part of a digital-physical community.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="final-verdict">
            <h2>Final Verdict: The Post-Corporate Era</h2>
            <p>
              The "Joe &amp; Val" model is more than a social media success story; it is the blueprint for the <strong>Post-Corporate Era</strong>. It proves that when you relinquish traditional control, you gain exponential reach. When you stop "polishing," you start "connecting." And most importantly, when you empower your people to be seen, your brand becomes impossible to ignore.
            </p>
            <p>
              <strong>The strategic mandate for Retail Executives and Franchise Owners is clear:</strong> Don't build a better ad campaign. Build a better culture, and let your people film it.
            </p>
          </section>

          <section className="blog-post-section" id="scaling-framework">
            <h2>Scaling the Framework: Don’t Just Watch the Trend</h2>
            <p>
              The bottleneck to scaling authenticity is governance. Most organizations fear granting freedom without losing brand
              integrity or safety compliance. This is the “scale paradox.”
            </p>
            <p>
              Our platform is built to make the Joe &amp; Val model replicable, safe, and measurable:
            </p>
            <ul>
              <li>
                <strong>Approval workflows:</strong> Bridge fastvertising and brand safety with real-time guardrails.
              </li>
              <li>
                <strong>Talent analytics:</strong> Track the protagonist effect with engagement + retention signals.
              </li>
              <li>
                <strong>Content vault:</strong> Centralize trending audio and safe-to-swap templates.
              </li>
            </ul>
            <p>
              Stop leaving your brand’s digital presence to chance. Build the Human Moat with enterprise-grade precision.
            </p>
          </section>

          <section className="blog-post-section" id="content-under-analysis" style={{ scrollMarginTop: 120 }}>
            <h2>List of content under analysis</h2>
            <p className="blog-small" style={{ marginTop: 0 }}>
              Links below are the videos referenced throughout the article.
            </p>
            <ol>
              {tikTokLinks.map((v) => (
                <li key={v.id} style={{ marginBottom: 12 }}>
                  <a href={v.url} target="_blank" rel="noreferrer">
                    {v.label}
                  </a>
                  <div className="blog-small">{v.note}</div>
                  <div className="blog-small">Stats: {v.stats.join(' • ')}</div>
                </li>
              ))}
            </ol>
          </section>

          <section className="blog-post-section blog-post-cta">
            <h2>Want help implementing EGC?</h2>
            <p>Reach out and we’ll show you how CrewCast can turn everyday moments into an always-on content engine.</p>
            <button className="blog-btn primary" onClick={() => navigate('/contact')}>
              Book a Strategy Audit
            </button>
          </section>
        </article>
      </main>
    </div>
  );
}

